module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<h3>Goal for this chapter</h3>\n<p>To match the schema from the first chapter, we have to extend a <code>Link</code> model with an additional field: <code>createdAt</code>.\nThis field will store information about date and time. The problem is that <code>H2</code> database understands only timestamp.\nSangria has a similar limitation - it supports only basic types. Our goal is to store the date an time information in the database\nand to present it in a human friendly format.</p>\n<h3>Extend a Link model</h3>\n<p>The type for storing date and time I chose is <code>akka.http.scaladsl.model.DateTime</code>.\nIt fits our example because it has implemented ISO Format converters.\n(This type covers all my needs without any additional work. So I chose it, but in real, production application avoid this if you can.\nJava has dedicated package for date and time and it includes many classes you can use.)</p>\n<Instruction>\n<p>Change the content of <code>Link.scala</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">//in imports:</span>\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>model<span class=\"token punctuation\">.</span>DateTime\n\n<span class=\"token comment\" spellcheck=\"true\">//replace current case class</span>\n<span class=\"token keyword\">case</span> <span class=\"token keyword\">class</span> Link<span class=\"token punctuation\">(</span>id<span class=\"token operator\">:</span> <span class=\"token builtin\">Int</span><span class=\"token punctuation\">,</span> url<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> description<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span><span class=\"token punctuation\">,</span> createdAt<span class=\"token operator\">:</span> DateTime<span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<h3>Fix the database</h3>\n<p>Currently in <code>DbSchema</code> we’re storing few links in database. We have to add the additional field.</p>\n<Instruction>\n<p>Add the <code>createdAt</code> field in the <code>Link</code> models for populated example data in <code>DBSchema</code>. Change the <code>databaseSetup</code> function into the following code:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code>Links forceInsertAll Seq<span class=\"token punctuation\">(</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">1</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"http://howtographql.com\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"Awesome community driven GraphQL tutorial\"</span><span class=\"token punctuation\">,</span> DateTime<span class=\"token punctuation\">(</span><span class=\"token number\">2017</span><span class=\"token punctuation\">,</span><span class=\"token number\">9</span><span class=\"token punctuation\">,</span><span class=\"token number\">12</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">2</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"http://graphql.org\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"Official GraphQL web page\"</span><span class=\"token punctuation\">,</span>DateTime<span class=\"token punctuation\">(</span><span class=\"token number\">2017</span><span class=\"token punctuation\">,</span><span class=\"token number\">10</span><span class=\"token punctuation\">,</span><span class=\"token number\">1</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">3</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"https://graphql.org/\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"GraphQL specification\"</span><span class=\"token punctuation\">,</span>DateTime<span class=\"token punctuation\">(</span><span class=\"token number\">2017</span><span class=\"token punctuation\">,</span><span class=\"token number\">10</span><span class=\"token punctuation\">,</span><span class=\"token number\">2</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Almost good, but <code>H2</code> doesn’t know how to store such type in database, so we will instruct it how to store it using built-in types.</p>\n<Instruction>\n<p>Add column mapper for our <code>DateTime</code> type in <code>DBSchema</code> object (before the <code>LinksTable</code> definition).</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">//ads to imports: </span>\n<span class=\"token keyword\">import</span> java<span class=\"token punctuation\">.</span>sql<span class=\"token punctuation\">.</span>Timestamp\n\n\n<span class=\"token comment\" spellcheck=\"true\">//and at the begining of the class' body:</span>\n<span class=\"token keyword\">implicit</span> <span class=\"token keyword\">val</span> dateTimeColumnType <span class=\"token operator\">=</span> MappedColumnType<span class=\"token punctuation\">.</span>base<span class=\"token punctuation\">[</span>DateTime<span class=\"token punctuation\">,</span> Timestamp<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n    dt <span class=\"token keyword\">=></span> <span class=\"token keyword\">new</span> Timestamp<span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">.</span>clicks<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n    ts <span class=\"token keyword\">=></span> DateTime<span class=\"token punctuation\">(</span>ts<span class=\"token punctuation\">.</span>getTime<span class=\"token punctuation\">)</span>\n<span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>This mapper will convert <code>DateTime</code> into <code>Long</code>, which is a primitive recognized by H2.</p>\n<p>The last thing is to add the <code>createdAt</code> column definition in the table declaration.</p>\n<Instruction>\n<p>Add the following code inside <code>LinksTable</code> class. Replace the current <code>*</code> function with the following one.</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">def</span> createdAt <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span>DateTime<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"CREATED_AT\"</span><span class=\"token punctuation\">)</span>\n\n<span class=\"token keyword\">def</span> <span class=\"token operator\">*</span> <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>id<span class=\"token punctuation\">,</span> url<span class=\"token punctuation\">,</span> description<span class=\"token punctuation\">,</span> createdAt<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>mapTo<span class=\"token punctuation\">[</span>Link<span class=\"token punctuation\">]</span>\n</code></pre>\n      </div>\n</Instruction>\n<hr>\n<p>In current state the <code>DBSchema</code> file should have following content:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n\n<span class=\"token keyword\">import</span> java<span class=\"token punctuation\">.</span>sql<span class=\"token punctuation\">.</span>Timestamp\n\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>model<span class=\"token punctuation\">.</span>DateTime\n<span class=\"token keyword\">import</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria<span class=\"token punctuation\">.</span>models<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> slick<span class=\"token punctuation\">.</span>jdbc<span class=\"token punctuation\">.</span>H2Profile<span class=\"token punctuation\">.</span>api<span class=\"token punctuation\">.</span>_\n\n<span class=\"token keyword\">import</span> scala<span class=\"token punctuation\">.</span>concurrent<span class=\"token punctuation\">.</span>duration<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> scala<span class=\"token punctuation\">.</span>concurrent<span class=\"token punctuation\">.</span>Await\n<span class=\"token keyword\">import</span> scala<span class=\"token punctuation\">.</span>language<span class=\"token punctuation\">.</span>postfixOps\n\n\n<span class=\"token keyword\">object</span> DBSchema <span class=\"token punctuation\">{</span>\n\n  <span class=\"token keyword\">implicit</span> <span class=\"token keyword\">val</span> dateTimeColumnType <span class=\"token operator\">=</span> MappedColumnType<span class=\"token punctuation\">.</span>base<span class=\"token punctuation\">[</span>DateTime<span class=\"token punctuation\">,</span> Timestamp<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n    dt <span class=\"token keyword\">=></span> <span class=\"token keyword\">new</span> Timestamp<span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">.</span>clicks<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n    ts <span class=\"token keyword\">=></span> DateTime<span class=\"token punctuation\">(</span>ts<span class=\"token punctuation\">.</span>getTime<span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">)</span>\n  \n  \n  <span class=\"token keyword\">class</span> LinksTable<span class=\"token punctuation\">(</span>tag<span class=\"token operator\">:</span> Tag<span class=\"token punctuation\">)</span> <span class=\"token keyword\">extends</span> Table<span class=\"token punctuation\">[</span>Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>tag<span class=\"token punctuation\">,</span> <span class=\"token string\">\"LINKS\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">{</span>\n\n    <span class=\"token keyword\">def</span> id <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span><span class=\"token builtin\">Int</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"ID\"</span><span class=\"token punctuation\">,</span> O<span class=\"token punctuation\">.</span>PrimaryKey<span class=\"token punctuation\">,</span> O<span class=\"token punctuation\">.</span>AutoInc<span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">def</span> url <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span><span class=\"token builtin\">String</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"URL\"</span><span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">def</span> description <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span><span class=\"token builtin\">String</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"DESCRIPTION\"</span><span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">def</span> createdAt <span class=\"token operator\">=</span> column<span class=\"token punctuation\">[</span>DateTime<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"CREATED_AT\"</span><span class=\"token punctuation\">)</span>\n\n    <span class=\"token keyword\">def</span> <span class=\"token operator\">*</span> <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>id<span class=\"token punctuation\">,</span> url<span class=\"token punctuation\">,</span> description<span class=\"token punctuation\">,</span> createdAt<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>mapTo<span class=\"token punctuation\">[</span>Link<span class=\"token punctuation\">]</span>\n\n  <span class=\"token punctuation\">}</span>\n\n  <span class=\"token keyword\">val</span> Links <span class=\"token operator\">=</span> TableQuery<span class=\"token punctuation\">[</span>LinksTable<span class=\"token punctuation\">]</span>\n\n  <span class=\"token comment\" spellcheck=\"true\">/**\n    * Load schema and populate sample data withing this Sequence od DBActions\n    */</span>\n  <span class=\"token keyword\">val</span> databaseSetup <span class=\"token operator\">=</span> DBIO<span class=\"token punctuation\">.</span>seq<span class=\"token punctuation\">(</span>\n    Links<span class=\"token punctuation\">.</span>schema<span class=\"token punctuation\">.</span>create<span class=\"token punctuation\">,</span>\n\n    Links forceInsertAll Seq<span class=\"token punctuation\">(</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">1</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"http://howtographql.com\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"Awesome community driven GraphQL tutorial\"</span><span class=\"token punctuation\">,</span> DateTime<span class=\"token punctuation\">(</span><span class=\"token number\">2017</span><span class=\"token punctuation\">,</span><span class=\"token number\">9</span><span class=\"token punctuation\">,</span><span class=\"token number\">12</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">2</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"http://graphql.org\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"Official GraphQL web page\"</span><span class=\"token punctuation\">,</span>DateTime<span class=\"token punctuation\">(</span><span class=\"token number\">2017</span><span class=\"token punctuation\">,</span><span class=\"token number\">10</span><span class=\"token punctuation\">,</span><span class=\"token number\">1</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Link<span class=\"token punctuation\">(</span><span class=\"token number\">3</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"https://graphql.org/\"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"GraphQL specification\"</span><span class=\"token punctuation\">,</span>DateTime<span class=\"token punctuation\">(</span><span class=\"token number\">2017</span><span class=\"token punctuation\">,</span><span class=\"token number\">10</span><span class=\"token punctuation\">,</span><span class=\"token number\">2</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">)</span>\n\n  <span class=\"token punctuation\">)</span>\n\n\n  <span class=\"token keyword\">def</span> createDatabase<span class=\"token operator\">:</span> DAO <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">val</span> db <span class=\"token operator\">=</span> Database<span class=\"token punctuation\">.</span>forConfig<span class=\"token punctuation\">(</span><span class=\"token string\">\"h2mem\"</span><span class=\"token punctuation\">)</span>\n\n    Await<span class=\"token punctuation\">.</span>result<span class=\"token punctuation\">(</span>db<span class=\"token punctuation\">.</span>run<span class=\"token punctuation\">(</span>databaseSetup<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span> <span class=\"token number\">10</span> seconds<span class=\"token punctuation\">)</span>\n\n    <span class=\"token keyword\">new</span> DAO<span class=\"token punctuation\">(</span>db<span class=\"token punctuation\">)</span>\n\n  <span class=\"token punctuation\">}</span>\n\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<h3>Define custom scalar for DateTime</h3>\n<p>Sangria supports scalars for basic GraphQL types like <code>String</code>, <code>Int</code>, etc.\nIn addition you can find scalars for types like <code>Long</code>, <code>BigInt</code> or <code>BigDecimal</code>.\nThere are a lot of them, but you might encounter situations where custom or unsupported types should be used.\nLike in our example, we use <code>DateTime</code> type and there are no built-in scalar for such.\nTo add support for our case, we will use the same trick as with H2.\nWe will define conversions between type we want and type Sangria understand and then back again to our type.\nThe best underlying type would be String, so we will use in our implementation.</p>\n<p>Let’s write a scalar that converts <code>String</code> to <code>DateTime</code> and vice versa.</p>\n<Instruction>\n<p>In <code>GraphQLSchema</code> add the following code:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">//imports:</span>\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>ast<span class=\"token punctuation\">.</span>StringValue\n\n<span class=\"token comment\" spellcheck=\"true\">//begining of the object's body:</span>\n<span class=\"token keyword\">implicit</span> <span class=\"token keyword\">val</span> GraphQLDateTime <span class=\"token operator\">=</span> ScalarType<span class=\"token punctuation\">[</span>DateTime<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token comment\" spellcheck=\"true\">//1</span>\n  <span class=\"token string\">\"DateTime\"</span><span class=\"token punctuation\">,</span><span class=\"token comment\" spellcheck=\"true\">//2</span>\n  coerceOutput <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">,</span> _<span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> dt<span class=\"token punctuation\">.</span>toString<span class=\"token punctuation\">,</span> <span class=\"token comment\" spellcheck=\"true\">//3</span>\n  coerceInput <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span> <span class=\"token comment\" spellcheck=\"true\">//4</span>\n    <span class=\"token keyword\">case</span> StringValue<span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">,</span> _<span class=\"token punctuation\">,</span> _ <span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> DateTime<span class=\"token punctuation\">.</span>fromIsoDateTimeString<span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>toRight<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">case</span> _ <span class=\"token keyword\">=></span> Left<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n  coerceUserInput <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span> <span class=\"token comment\" spellcheck=\"true\">//5</span>\n    <span class=\"token keyword\">case</span> s<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span> <span class=\"token keyword\">=></span> DateTime<span class=\"token punctuation\">.</span>fromIsoDateTimeString<span class=\"token punctuation\">(</span>s<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>toRight<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n    <span class=\"token keyword\">case</span> _ <span class=\"token keyword\">=></span> Left<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<ol>\n<li>Use <code>implicit</code> because it implicitly has to be in scope</li>\n<li><code>\"DateTime\"</code>. The name will be used in schemas.</li>\n<li><code>coerceOutput</code> converts our type to a String. It will be used to produce the output data.</li>\n<li><code>coerceInput</code> needs a partial function with <code>Value</code> as single argument. Such value could be of many types. In our case we’re parsing only from <code>StringValue</code>. Of course nothing stops you from defining many conversions. If you define more cases for coerceInput, users will have the freedom to provide input in more ways.</li>\n<li><code>coerceUserInput</code> converts Literal which almost always is a String. While this function should cover basic types, <code>coerceInput</code> and <code>coerceOutput</code> should always be a value that the GraphQL grammar supports.</li>\n</ol>\n<p>Both functions <code>coerceInput</code> and <code>coerceUserInput</code> should respond with <code>Either</code>. The correct (right) value should consist an object of expected type.\nIn case of failure, the left value should contain a <code>Violation</code> subtype. Sangria provides many <code>Violation</code> subtypes, but in the code above you can see I’ve used <code>DateTimeCoerceViolation</code>.\nLet’s implement this.</p>\n<Instruction>\n<p>Add in the models package object the following definition:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token comment\" spellcheck=\"true\">//imports header:</span>\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>validation<span class=\"token punctuation\">.</span>Violation\n\n<span class=\"token comment\" spellcheck=\"true\">//package body:</span>\n  <span class=\"token keyword\">case</span> <span class=\"token keyword\">object</span> DateTimeCoerceViolation <span class=\"token keyword\">extends</span> Violation <span class=\"token punctuation\">{</span>\n    <span class=\"token keyword\">override</span> <span class=\"token keyword\">def</span> errorMessage<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span> <span class=\"token operator\">=</span> <span class=\"token string\">\"Error during parsing DateTime\"</span>\n  <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Finally, add the new field definition to the <code>LinkType</code>.</p>\n<Instruction>\n<p>replace <code>LinkType</code> definition in the <code>GraphQLSchema</code> file with the following piece of code:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code>   <span class=\"token keyword\">val</span> LinkType <span class=\"token operator\">=</span> ObjectType<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n      <span class=\"token string\">\"Link\"</span><span class=\"token punctuation\">,</span>\n      fields<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n        Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"id\"</span><span class=\"token punctuation\">,</span> IntType<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>id<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n        Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"url\"</span><span class=\"token punctuation\">,</span> StringType<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>url<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n        Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"description\"</span><span class=\"token punctuation\">,</span> StringType<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>description<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n        Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"createdAt\"</span><span class=\"token punctuation\">,</span> GraphQLDateTime<span class=\"token punctuation\">,</span> resolve<span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>createdAt<span class=\"token punctuation\">)</span>\n      <span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>In case you’ve used derived version of LinkType you have to manage this additional field another way.\nBecause Macro don’t know the type you want to use you have to define it explicitly. Good news is that you have to define only this field - the rest\nis still managed by macro.</p>\n<p>In case of derived LinkType, the definition should looks like this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code>  <span class=\"token keyword\">val</span> LinkType <span class=\"token operator\">=</span> deriveObjectType<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n    ReplaceField<span class=\"token punctuation\">(</span><span class=\"token string\">\"createdAt\"</span><span class=\"token punctuation\">,</span> Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"createdAt\"</span><span class=\"token punctuation\">,</span> GraphQLDateTime<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>createdAt<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">)</span>\n</code></pre>\n      </div>\n<hr>\n<p>After the last changes <code>GraphQLSchema</code> file should looks like this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-scala\"   nocopy=\"false\"><code><span class=\"token keyword\">package</span> com<span class=\"token punctuation\">.</span>howtographql<span class=\"token punctuation\">.</span>scala<span class=\"token punctuation\">.</span>sangria\n\n<span class=\"token keyword\">import</span> akka<span class=\"token punctuation\">.</span>http<span class=\"token punctuation\">.</span>scaladsl<span class=\"token punctuation\">.</span>model<span class=\"token punctuation\">.</span>DateTime\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>schema<span class=\"token punctuation\">.</span><span class=\"token punctuation\">{</span>ListType<span class=\"token punctuation\">,</span> ObjectType<span class=\"token punctuation\">}</span>\n<span class=\"token keyword\">import</span> models<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>ast<span class=\"token punctuation\">.</span>StringValue\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>execution<span class=\"token punctuation\">.</span>deferred<span class=\"token punctuation\">.</span><span class=\"token punctuation\">{</span>DeferredResolver<span class=\"token punctuation\">,</span> Fetcher<span class=\"token punctuation\">,</span> HasId<span class=\"token punctuation\">}</span>\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>schema<span class=\"token punctuation\">.</span>_\n<span class=\"token keyword\">import</span> sangria<span class=\"token punctuation\">.</span>macros<span class=\"token punctuation\">.</span>derive<span class=\"token punctuation\">.</span>_\n\n<span class=\"token keyword\">object</span> GraphQLSchema <span class=\"token punctuation\">{</span>\n\n  <span class=\"token keyword\">implicit</span> <span class=\"token keyword\">val</span> GraphQLDateTime <span class=\"token operator\">=</span> ScalarType<span class=\"token punctuation\">[</span>DateTime<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span><span class=\"token comment\" spellcheck=\"true\">//1</span>\n    <span class=\"token string\">\"DateTime\"</span><span class=\"token punctuation\">,</span><span class=\"token comment\" spellcheck=\"true\">//2</span>\n    coerceOutput <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">,</span> _<span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> dt<span class=\"token punctuation\">.</span>toString<span class=\"token punctuation\">,</span> <span class=\"token comment\" spellcheck=\"true\">//3</span>\n    coerceInput <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span> <span class=\"token comment\" spellcheck=\"true\">//4</span>\n      <span class=\"token keyword\">case</span> StringValue<span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">,</span> _<span class=\"token punctuation\">,</span> _ <span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> DateTime<span class=\"token punctuation\">.</span>fromIsoDateTimeString<span class=\"token punctuation\">(</span>dt<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>toRight<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n      <span class=\"token keyword\">case</span> _ <span class=\"token keyword\">=></span> Left<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n    coerceUserInput <span class=\"token operator\">=</span> <span class=\"token punctuation\">{</span> <span class=\"token comment\" spellcheck=\"true\">//5</span>\n      <span class=\"token keyword\">case</span> s<span class=\"token operator\">:</span> <span class=\"token builtin\">String</span> <span class=\"token keyword\">=></span> DateTime<span class=\"token punctuation\">.</span>fromIsoDateTimeString<span class=\"token punctuation\">(</span>s<span class=\"token punctuation\">)</span><span class=\"token punctuation\">.</span>toRight<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n      <span class=\"token keyword\">case</span> _ <span class=\"token keyword\">=></span> Left<span class=\"token punctuation\">(</span>DateTimeCoerceViolation<span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">)</span>\n\n\n  <span class=\"token keyword\">val</span> LinkType <span class=\"token operator\">=</span> deriveObjectType<span class=\"token punctuation\">[</span><span class=\"token builtin\">Unit</span><span class=\"token punctuation\">,</span> Link<span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n    ReplaceField<span class=\"token punctuation\">(</span><span class=\"token string\">\"createdAt\"</span><span class=\"token punctuation\">,</span> Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"createdAt\"</span><span class=\"token punctuation\">,</span> GraphQLDateTime<span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> _<span class=\"token punctuation\">.</span>value<span class=\"token punctuation\">.</span>createdAt<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">)</span>\n\n  <span class=\"token keyword\">implicit</span> <span class=\"token keyword\">val</span> linkHasId <span class=\"token operator\">=</span> HasId<span class=\"token punctuation\">[</span>Link<span class=\"token punctuation\">,</span> <span class=\"token builtin\">Int</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>_<span class=\"token punctuation\">.</span>id<span class=\"token punctuation\">)</span>\n\n  <span class=\"token keyword\">val</span> linksFetcher <span class=\"token operator\">=</span> Fetcher<span class=\"token punctuation\">(</span>\n    <span class=\"token punctuation\">(</span>ctx<span class=\"token operator\">:</span> MyContext<span class=\"token punctuation\">,</span> ids<span class=\"token operator\">:</span> Seq<span class=\"token punctuation\">[</span><span class=\"token builtin\">Int</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">)</span> <span class=\"token keyword\">=></span> ctx<span class=\"token punctuation\">.</span>dao<span class=\"token punctuation\">.</span>getLinks<span class=\"token punctuation\">(</span>ids<span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">)</span>\n\n  <span class=\"token keyword\">val</span> Resolver <span class=\"token operator\">=</span> DeferredResolver<span class=\"token punctuation\">.</span>fetchers<span class=\"token punctuation\">(</span>linksFetcher<span class=\"token punctuation\">)</span>\n\n\n  <span class=\"token keyword\">val</span> Id <span class=\"token operator\">=</span> Argument<span class=\"token punctuation\">(</span><span class=\"token string\">\"id\"</span><span class=\"token punctuation\">,</span> IntType<span class=\"token punctuation\">)</span>\n  <span class=\"token keyword\">val</span> Ids <span class=\"token operator\">=</span> Argument<span class=\"token punctuation\">(</span><span class=\"token string\">\"ids\"</span><span class=\"token punctuation\">,</span> ListInputType<span class=\"token punctuation\">(</span>IntType<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n\n  <span class=\"token keyword\">val</span> QueryType <span class=\"token operator\">=</span> ObjectType<span class=\"token punctuation\">(</span>\n    <span class=\"token string\">\"Query\"</span><span class=\"token punctuation\">,</span>\n    fields<span class=\"token punctuation\">[</span>MyContext<span class=\"token punctuation\">,</span> <span class=\"token builtin\">Unit</span><span class=\"token punctuation\">]</span><span class=\"token punctuation\">(</span>\n      Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"allLinks\"</span><span class=\"token punctuation\">,</span> ListType<span class=\"token punctuation\">(</span>LinkType<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span> resolve <span class=\"token operator\">=</span> c <span class=\"token keyword\">=></span> c<span class=\"token punctuation\">.</span>ctx<span class=\"token punctuation\">.</span>dao<span class=\"token punctuation\">.</span>allLinks<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"link\"</span><span class=\"token punctuation\">,</span>\n        OptionType<span class=\"token punctuation\">(</span>LinkType<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n        arguments <span class=\"token operator\">=</span> Id <span class=\"token operator\">:</span><span class=\"token operator\">:</span> Nil<span class=\"token punctuation\">,</span>\n        resolve <span class=\"token operator\">=</span> c <span class=\"token keyword\">=></span> linksFetcher<span class=\"token punctuation\">.</span>deferOpt<span class=\"token punctuation\">(</span>c<span class=\"token punctuation\">.</span>arg<span class=\"token punctuation\">(</span>Id<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n      <span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n      Field<span class=\"token punctuation\">(</span><span class=\"token string\">\"links\"</span><span class=\"token punctuation\">,</span>\n        ListType<span class=\"token punctuation\">(</span>LinkType<span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n        arguments <span class=\"token operator\">=</span> Ids <span class=\"token operator\">:</span><span class=\"token operator\">:</span> Nil<span class=\"token punctuation\">,</span>\n        resolve <span class=\"token operator\">=</span> c <span class=\"token keyword\">=></span> linksFetcher<span class=\"token punctuation\">.</span>deferSeq<span class=\"token punctuation\">(</span>c<span class=\"token punctuation\">.</span>arg<span class=\"token punctuation\">(</span>Ids<span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n      <span class=\"token punctuation\">)</span>\n    <span class=\"token punctuation\">)</span>\n  <span class=\"token punctuation\">)</span>\n\n  <span class=\"token keyword\">val</span> SchemaDefinition <span class=\"token operator\">=</span> Schema<span class=\"token punctuation\">(</span>QueryType<span class=\"token punctuation\">)</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p>Now when you’ll add <code>createdAt</code> field to the query, you should get proper response. For example on query:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code><span class=\"token keyword\">query</span> <span class=\"token punctuation\">{</span>\n\n  link<span class=\"token punctuation\">(</span><span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> <span class=\"token number\">1</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">{</span>\n    \tid\n    \turl\n    \tcreatedAt\n  \t<span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p>You will get a response:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-json\"   nocopy=\"false\"><code><span class=\"token punctuation\">{</span>\n  <span class=\"token property\">\"data\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n    <span class=\"token property\">\"link\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n      <span class=\"token property\">\"id\"</span><span class=\"token operator\">:</span> <span class=\"token number\">1</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"url\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"http://howtographql.com\"</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"createdAt\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"2017-09-12T00:00:00\"</span>\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p>Now you know the basics. In the next chapter you will add additional models. We will extract some common parts as interface to keep them in one place.</p>",
			"fields": {
				"slug": "/graphql-scala/5-custom-scalars/"
			},
			"excerpt": "Goal for this chapter To match the schema from the first chapter, we have to extend a   model with an additional field:  . \nThis field will...",
			"frontmatter": {
				"title": "Custom Scalars",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/opt/build/repo/content/backend/graphql-scala/5-custom-scalars.md absPath of file",
				"question": "What is a main advantage of defining a custom scalar type?",
				"answers": [
					"It adds bells and whistles to your code.",
					"It makes possible to parse values type you want to",
					"It's only an alias for basic types, there is nothing more than improving readability",
					"You can store your data in a type not supported by the database."
				],
				"correctAnswer": 1,
				"description": "In this chapter Link model will get an additional field to store date and time. You will learn how to handle such custom types and use scalars.",
				"pageTitle": "GraphQL Scala - Scalars"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-elixir/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Elixir & Absinthe as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-elixir/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with Absinthe, implement query resolvers in Elixir and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-elixir/4-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with gqlgen."
						},
						"fields": {
							"slug": "/graphql-go/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Absinthe & Elixir. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "what are queries and implementing a query in gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Creating an app with gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "what are mutations and implementing a mutation in gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Create and Retrieve Links",
							"description": "Enable Users to create Links and see them"
						},
						"fields": {
							"slug": "/graphql-go/5-create-and-retrieve-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Auth Endpoints",
							"description": "Enable Users to register, login and refresh their token"
						},
						"fields": {
							"slug": "/graphql-go/7-auth-endpoints/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-java/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Summary of building a GraphQL server with GO backend"
						},
						"fields": {
							"slug": "/graphql-go/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with a Node.js & Express GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/10-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Logged in User",
							"description": "Get logged-in user in gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/8-logged-in-user-object/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Implement authentication in gqlgen with jwt"
						},
						"fields": {
							"slug": "/graphql-go/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-java/12-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-java, implement query resolvers in Java and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Alternative approaches to schema development",
							"description": "Learn about an alternative approach to developing schemas with graphl-java with graphql-spqr"
						},
						"fields": {
							"slug": "/graphql-java/11-alternative-approaches/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-java and best practices for defining the GraphQL schema in this tutorial."
						},
						"fields": {
							"slug": "/graphql-java/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Database",
							"description": "Setup mysql db with migrations in golang"
						},
						"fields": {
							"slug": "/graphql-go/4-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-java & Java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connectors",
							"description": "Learn how to write connectors in Java to load data from MongoDB with graphql-java and a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/4-connectors/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Java and graphql-java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/6-more-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Java."
						},
						"fields": {
							"slug": "/graphql-java/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with apollo-server, Node.js & Prisma and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Node.js & Express as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-js/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Java, Node.js & Express."
						},
						"fields": {
							"slug": "/graphql-java/7-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Subscriptions",
							"description": "Learn how to implement server-side GraphQL subscriptions with Java to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-java/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting The Server and Database with Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is accessed using Prisma Client."
						},
						"fields": {
							"slug": "/graphql-js/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/9-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Setting up a Django Graphene Project"
						},
						"fields": {
							"slug": "/graphql-python/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-js/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Building a Server with GraphQL and Graphene"
						},
						"fields": {
							"slug": "/graphql-python/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Tutorial Summary"
						},
						"fields": {
							"slug": "/graphql-python/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Creating Your First Queries"
						},
						"fields": {
							"slug": "/graphql-python/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Creating Data With Mutations"
						},
						"fields": {
							"slug": "/graphql-python/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Authenticating on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Handling Errors on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Filtering GraphQL Queries"
						},
						"fields": {
							"slug": "/graphql-python/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Simple Pagination with Graphene"
						},
						"fields": {
							"slug": "/graphql-python/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Links and Voting",
							"description": "Enable Users to create Links and to Vote on them"
						},
						"fields": {
							"slug": "/graphql-python/5-links-and-voting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-ruby and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-ruby/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-ruby and best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-ruby/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relay",
							"description": "Using Relay on Graphene"
						},
						"fields": {
							"slug": "/graphql-python/9-relay/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-ruby, implement query resolvers in Ruby and use a GraphiQL Playground to test your queries."
						},
						"fields": {
							"slug": "/graphql-ruby/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Nodes",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/5-connecting-nodes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/graphql-scala/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "In this chapter will be described how to setup the HTTP server, install all dependencies and setup the database."
						},
						"fields": {
							"slug": "/graphql-scala/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Handling Arguments",
							"description": "In this chapter you will learn how to use arguments and how to handle them and pass to the business logic."
						},
						"fields": {
							"slug": "/graphql-scala/3-handling-arguments/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "The first query",
							"description": "In this chapter you will learn last steps of preparation, add graphiql console for easy debugging and at the end, run your first query."
						},
						"fields": {
							"slug": "/graphql-scala/2-preparing-first-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deferred Resolvers",
							"description": "In this chapter you will learn how to improve performance with deferred resolvers."
						},
						"fields": {
							"slug": "/graphql-scala/4-deferred-resolvers/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Custom Scalars",
							"description": "In this chapter Link model will get an additional field to store date and time. You will learn how to handle such custom types and use scalars."
						},
						"fields": {
							"slug": "/graphql-scala/5-custom-scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Interfaces",
							"description": "In this chapter we will introduce two additional models. We show the most common parts and extract those as an interface. This time you will work alone."
						},
						"fields": {
							"slug": "/graphql-scala/6-interfaces/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "In this chapter you will learn how to add data to the database."
						},
						"fields": {
							"slug": "/graphql-scala/8-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-ruby & Ruby and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-ruby/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Sangria's Middleware allows pre-processing queries. Gather all useful data in the following subqueries."
						},
						"fields": {
							"slug": "/graphql-scala/9-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Apollo, TypeScript, Nexus & Prisma and best practices for authentication, filtering and pagination."
						},
						"fields": {
							"slug": "/typescript-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relations",
							"description": "In this chapter you will learn how to connect models with relations. You will also learn how to prepare fetchers to use either relations or ids to get entities."
						},
						"fields": {
							"slug": "/graphql-scala/7-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with Nexus and TypeScript. Test your implementation in Apollo Studio Explorer."
						},
						"fields": {
							"slug": "/typescript-apollo/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with TypeScript and best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/typescript-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting The Server and Database with Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is accessed using Prisma Client."
						},
						"fields": {
							"slug": "/typescript-apollo/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with TypeScript & Apollo as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/typescript-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with TypeScript, Apollo Server & Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying to Production",
							"description": "Inside ."
						},
						"fields": {
							"slug": "/typescript-apollo/9-deployment/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Node.js, Fastify, TypeScript, GraphQL-Helix & Prisma 3 and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/typescript-helix/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Project Setup",
							"description": "Learn how to setup a basic NodeJS & TypeScript project"
						},
						"fields": {
							"slug": "/typescript-helix/1-project-setup/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication and pagination"
						},
						"fields": {
							"slug": "/typescript-helix/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Fastify & Prisma."
						},
						"fields": {
							"slug": "/typescript-helix/10-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Voting and Custom GraphQL Scalars",
							"description": "Learn how to implement a voting feature and use custom scalars for a Hackernews Clone with Apollo Server, Nexus and Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/7-voting-and-scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with TypeScript, Apollo Server & Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Creating a GraphQL Server",
							"description": "Getting Started with a GraphQL server based on Fastify and GraphQL-Helix"
						},
						"fields": {
							"slug": "/typescript-helix/3-graphql-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema with Nexus, implement query resolvers and test your queries in Apollo Studio Explorer"
						},
						"fields": {
							"slug": "/typescript-apollo/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how write a GraphQL schema and how to try and run it."
						},
						"fields": {
							"slug": "/typescript-helix/2-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphiQL."
						},
						"fields": {
							"slug": "/typescript-helix/4-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphiQL."
						},
						"fields": {
							"slug": "/typescript-helix/5-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Angular & Apollo Client. You'll use angular-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/angular-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting The Server and Database with Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is accessed using Prisma Client."
						},
						"fields": {
							"slug": "/typescript-helix/7-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with angular-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Angular & Apollo Client you learned how to build a production-ready Hackernews clone using angular-cli & Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/typescript-helix/6-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, TypeScript, Fastify & Prisma."
						},
						"fields": {
							"slug": "/typescript-helix/8-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with Angular and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/angular-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading link-items",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's service injected in the component to define and send mutations."
						},
						"fields": {
							"slug": "/angular-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/typescript-helix/9-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Angular app with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Ember, and Apollo Client. You'll use Ember and ember-apollo-client for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/ember-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/angular-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & Angular. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/angular-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use Angular Router  together with GraphQL and Apollo Client to implement navigation in an Angular app."
						},
						"fields": {
							"slug": "/angular-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a Angular app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/angular-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Ember & Apollo Client you learned how to build a production-ready Hackernews clone using ember-apollo-client & Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/8-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/ember-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with ember-cli, ember-apollo-client, and the backend with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigation",
							"description": "Learn how to use Ember components together with GraphQL and Apollo Client to implement navigation in an Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/4-navigation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React and Apollo Client we learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/ember-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React and Apollo Client. Use create-react-app for the frontend and Apollo Server with Prisma for the backend."
						},
						"fields": {
							"slug": "/react-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use GraphQL mutations with Apollo Client. Use Apollo's `<Mutation />` component to define and send mutations."
						},
						"fields": {
							"slug": "/react-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use React Router 5 together with GraphQL and Apollo Client to implement navigation in a React app. Each route will be represented as a `Link`."
						},
						"fields": {
							"slug": "/react-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a production-ready Hackernews clone with GraphQL, React & Relay. You'll use create-react-app for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/react-relay/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Ember app with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL and Apollo Client to provide an email/password login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use `ember-apollo-client` to send mutations."
						},
						"fields": {
							"slug": "/ember-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Prisma provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/react-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a React app. The Prisma API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/react-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically be reflected in our React components."
						},
						"fields": {
							"slug": "/react-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL Fragments and Relay's QueryRenderer to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-relay/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use Relay Modern's new Mutation API to send mutations to a GraphQL server. With Relay Modern you can also update the cache directly after the mutation."
						},
						"fields": {
							"slug": "/react-relay/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": null
						},
						"fields": {
							"slug": "/react-relay/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Relay Modern you learned how to build a production-ready Hackernews clone using create-react-app & Graphcool."
						},
						"fields": {
							"slug": "/react-relay/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Relay Modern's imperative API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-relay/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Relay to provide an email-and-password-based login in a React app with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React & urql. Use create-react-app for the frontend and graphql-yoga & Prisma for the backend."
						},
						"fields": {
							"slug": "/react-urql/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with urql to load data from a GraphQL API and display it in your React components."
						},
						"fields": {
							"slug": "/react-urql/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement cursor-based pagination with GraphQL and Relay Modern using `PaginationContainer`. Relay uses the concept of connections to represent lists."
						},
						"fields": {
							"slug": "/react-relay/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Relay Modern & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-relay/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-urql/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with urql. Use urql's 'useMutation' hook to define and send mutations."
						},
						"fields": {
							"slug": "/react-urql/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn how to implement authentication with GraphQL & urql to provide an email-&-password login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-urql/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination and Cache Updates",
							"description": "Learn how to implement limit-offset pagination and cache updates with GraphQL and urql in a React app and update."
						},
						"fields": {
							"slug": "/react-urql/7-pagination-and-cache-updates/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Queries",
							"description": "Learn more about mutations and queries with React & urql.."
						},
						"fields": {
							"slug": "/react-urql/6-more-mutations-and-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & urql you learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-urql/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with urql & React. The websockets transport will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-urql/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, VueJS & Apollo Client. You'll use vue-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/vue-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use react-router 5 together with GraphQL and urql to implement navigation in a React app. Each route will be represented as a Link."
						},
						"fields": {
							"slug": "/react-urql/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with vue-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with VueJS & Apollo Client you learned how to build a production-ready Hackernews clone using vue-cli & Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with VueJS and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/vue-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use vue-router together with GraphQL and Apollo Client to implement navigation in a VueJS app."
						},
						"fields": {
							"slug": "/vue-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a VueJS app with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & VueJS. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/vue-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/vue-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a VueJS app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/vue-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `mutate` method to define and send mutations."
						},
						"fields": {
							"slug": "/vue-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Clients",
							"description": "Learn about GraphQL clients, like Relay and Apollo Client, and the features they provide, such as caching, schema validation and query/view colocation."
						},
						"fields": {
							"slug": "/advanced/0-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Server",
							"description": "Learn about the major concepts of GraphQL servers, how it executes GraphQL queries with resolver functions and batches multiple requests."
						},
						"fields": {
							"slug": "/advanced/1-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Tooling and Ecosystem",
							"description": "Learn about the GraphQL ecosystem and its awesome tooling like the GraphiQL playgrounds or self-generating docs through introspection."
						},
						"fields": {
							"slug": "/advanced/3-tooling-and-ecosystem/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More GraphQL Concepts",
							"description": "Learn about advanced concepts of the GraphQL language like fragments, query arguments, aliases, interfaces and more SDL features"
						},
						"fields": {
							"slug": "/advanced/2-more-graphql-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common GraphQL questions about server-side caching, authentication, authorization, error handling, offline usage and more."
						},
						"fields": {
							"slug": "/advanced/5-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GraphQL is the better REST",
							"description": "Learn why GraphQL is a more efficient and flexible alternative to REST APIs. It has a strong type system and avoids frontend issues like over- and underfetching."
						},
						"fields": {
							"slug": "/basics/1-graphql-is-the-better-rest/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Security",
							"description": "Learn about different security aspects and strategies for GraphQL, such as timeouts, maximum query depth, query complexity and throttling."
						},
						"fields": {
							"slug": "/advanced/4-security/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Architecture)",
							"description": "Learn about different architectural use cases of GraphQL and the major components on the backend and the frontend, like resolver functions and client libraries."
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Core Concepts",
							"description": "Learn about the basic GraphQL language concepts, such as Queries, Mutations, Subscriptions and the GraphQL Schema & SDL."
						},
						"fields": {
							"slug": "/basics/2-core-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about GraphQL and why it is an API technology that's superior to REST. It is not only for React & Javascript developers but can be used for any API."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/graphql-scala/5-custom-scalars/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/graphql-scala-5-custom-scalars.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/graphql-scala-5-custom-scalars.json
// module chunks = 75